import {
  normalizeSearch,
  type RaMasterKind,
  type RaMasterStatus,
  type RaMatrixDefinition,
  type RiskControl,
  type RiskHazard,
  type RiskRating,
  raAppliesTo,
  raLinkRatings,
} from '@felix/domain';
import type { RaMasterItem, RaMasterView } from './types.js';

/**
 * Risk ▸ Master Data ekranının saf yardımcıları (K2; ADR 0086): süzgeç, arama, gemi RA formuna
 * faaliyetten öneri. Öneri yalnız etkin kayıtlardan gelir; kalan risk ilk riske eşit başlar
 * (azalma varsayılmaz; değerlendiren önlemlerden sonra düşürür, gerekçesiyle).
 */

export interface MasterFilter {
  query: string;
  status: 'all' | RaMasterStatus;
  /** Gemi grubu (kategori ve faaliyet). */
  group: string;
  /** Kategori (faaliyet ve bağ). */
  category: string;
}

export const EMPTY_MASTER_FILTER: MasterFilter = {
  query: '',
  status: 'all',
  group: 'all',
  category: 'all',
};

type AnyItem = RaMasterItem<RaMasterKind>;

/** Kayıt kimliğinden kayda (tür başına). */
export function indexOf(view: RaMasterView) {
  const map = <T extends { id: string }>(list: readonly T[]) => new Map(list.map((i) => [i.id, i]));
  return {
    categories: map(view.categories),
    activities: map(view.activities),
    hazards: map(view.hazards),
    consequences: map(view.consequences),
    controls: map(view.controls),
  };
}

/** Kaydın aranan metni: kod, ad ve türün öne çıkan alanları. */
function searchText(view: RaMasterView, kind: RaMasterKind, item: AnyItem): string {
  const index = indexOf(view);
  const v = item as unknown as Record<string, unknown>;
  const parts = [String(v.code ?? ''), String(v.name ?? ''), String(v.measure ?? '')];
  if (kind === 'activities') parts.push(String(v.scope ?? ''));
  if (kind === 'hazards') parts.push(String(v.family ?? ''));
  if (kind === 'links') {
    const link = item as RaMasterItem<'links'>;
    parts.push(
      index.activities.get(link.activityId)?.name ?? '',
      index.activities.get(link.activityId)?.code ?? '',
      index.hazards.get(link.hazardId)?.name ?? '',
      index.hazards.get(link.hazardId)?.code ?? '',
    );
  }
  return normalizeSearch(parts.join(' '));
}

/** Kaydın kategorisi (faaliyet ve bağ); diğer türlerde `null`. */
function categoryOf(view: RaMasterView, kind: RaMasterKind, item: AnyItem): string | null {
  if (kind === 'activities') return (item as RaMasterItem<'activities'>).categoryId;
  if (kind === 'links') {
    const link = item as RaMasterItem<'links'>;
    return view.activities.find((a) => a.id === link.activityId)?.categoryId ?? null;
  }
  return null;
}

/** Sekmenin satırları: arama, durum, gemi grubu ve kategori süzgeci. */
export function filterMaster<K extends RaMasterKind>(
  view: RaMasterView,
  kind: K,
  filter: MasterFilter,
): RaMasterItem<K>[] {
  const q = normalizeSearch(filter.query.trim());
  const list = view[kind] as unknown as RaMasterItem<K>[];
  return list.filter((item) => {
    const any = item as unknown as AnyItem;
    if (filter.status !== 'all' && any.status !== filter.status) return false;
    if (filter.group !== 'all' && (kind === 'categories' || kind === 'activities')) {
      const groups = (item as unknown as { vesselGroups: string[] }).vesselGroups;
      if (!raAppliesTo(groups, filter.group === 'ALL' ? null : filter.group)) return false;
      if (filter.group === 'ALL' && !groups.includes('ALL')) return false;
    }
    if (filter.category !== 'all' && (kind === 'activities' || kind === 'links')) {
      if (categoryOf(view, kind, any) !== filter.category) return false;
    }
    return !q || searchText(view, kind, any).includes(q);
  });
}

/** Gemiye uygulanan etkin faaliyetler, koda göre (gemi RA formunun seçimi). */
export function activityOptions(
  view: RaMasterView,
  vesselGroup: string | null | undefined,
): RaMasterItem<'activities'>[] {
  const categories = new Map(view.categories.map((c) => [c.id, c]));
  return view.activities
    .filter((a) => a.status === 'active' && raAppliesTo(a.vesselGroups, vesselGroup))
    .filter((a) => categories.get(a.categoryId)?.status !== 'retired')
    .sort((a, b) => a.code.localeCompare(b.code));
}

export interface RaSuggestion {
  link: RaMasterItem<'links'>;
  hazard: RaMasterItem<'hazards'>;
  consequences: string[];
  existing: RaMasterItem<'controls'>[];
  additional: RaMasterItem<'controls'>[];
  initial: RiskRating;
  residual: RiskRating | null;
}

/** Faaliyetin önerileri: etkin bağ ve etkin tehlike; yalnız etkin kontroller. */
export function activitySuggestions(
  view: RaMasterView,
  activityId: string,
  matrix: RaMatrixDefinition,
): RaSuggestion[] {
  const index = indexOf(view);
  const active = <T extends { status: string }>(item: T | undefined): item is T =>
    item !== undefined && item.status === 'active';
  const controls = (ids: readonly string[]) =>
    ids.map((id) => index.controls.get(id)).filter(active);
  return view.links
    .filter((l) => l.activityId === activityId && l.status === 'active')
    .flatMap((link) => {
      const hazard = index.hazards.get(link.hazardId);
      if (!active(hazard)) return [];
      const ratings = raLinkRatings(link, matrix);
      // Etkin bağın ilk riski vardır; değerlendirilmemiş taslak öneri sunulmaz (ADR 0098 §4)
      if (!ratings.initial) return [];
      const initial = ratings.initial;
      return [
        {
          link,
          hazard,
          consequences: hazard.consequenceIds
            .map((id) => index.consequences.get(id))
            .filter((c) => c !== undefined && c.status !== 'retired')
            .map((c) => String(c?.name)),
          existing: controls(link.existingControlIds),
          additional: controls(link.additionalControlIds),
          initial,
          residual: ratings.residual,
        },
      ];
    })
    .sort((a, b) => b.initial.score - a.initial.score || a.link.code.localeCompare(b.link.code));
}

const clip = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

/**
 * Seçilen önerileri gemi RA içeriğine çevirir (soru 8: gemi kendi RA'sını yazar, şirket
 * kütüphanesinden başlar). Tehlike: ad, sonuçlar, ilk L×S; kalan risk ilk riske eşit (azalma
 * varsayılmaz; XL W-02). Önlem: mevcut önlem "open" (yerinde olduğu gemide doğrulanır; seçmek
 * uygulandı demek değildir, FD §5 Tab 4), ek önlem "planned"; tür birincil hiyerarşi.
 */
export function suggestionsToContent(
  suggestions: readonly RaSuggestion[],
  newId: () => string,
): { hazards: RiskHazard[]; controls: RiskControl[] } {
  const hazards: RiskHazard[] = [];
  const controls: RiskControl[] = [];
  for (const s of suggestions) {
    const hazardId = newId();
    hazards.push({
      id: hazardId,
      hazard: clip(s.hazard.name, 300),
      consequence: clip(s.consequences.join('; '), 500),
      // Öneri yalnız ilk riski olan etkin bağdan gelir (`activitySuggestions`)
      likelihood: s.initial.likelihood,
      severity: s.initial.severity,
      residualLikelihood: s.link.residualLikelihood ?? s.initial.likelihood,
      residualSeverity: s.link.residualSeverity ?? s.initial.severity,
    });
    for (const [list, status] of [
      [s.existing, 'open'],
      [s.additional, 'planned'],
    ] as const) {
      for (const control of list) {
        controls.push({
          id: newId(),
          hazardId,
          type: control.hierarchy[0] ?? 'administrative',
          measure: clip(control.measure, 300),
          responsible: clip(control.responsibleRole ?? '', 120),
          dueDay: null,
          status,
        });
      }
    }
  }
  return { hazards, controls };
}

/** Sekmede taslak durumundaki satırlar (toplu etkinleştirme). */
export function draftsOf<K extends RaMasterKind>(items: readonly RaMasterItem<K>[]) {
  return items.filter((i) => (i as unknown as AnyItem).status === 'draft');
}

/** Bağın etiketi: faaliyet kodu ve tehlike adı. */
export function linkTitle(view: RaMasterView, link: RaMasterItem<'links'>): string {
  const index = indexOf(view);
  const activity = index.activities.get(link.activityId);
  const hazard = index.hazards.get(link.hazardId);
  return `${activity?.code ?? '—'} · ${hazard?.name ?? '—'}`;
}
